// One time axis: decimal years, astronomical numbering (0 = 1 BC), proleptic Gregorian calendar.
// Mirrors pipeline/conflicts_pipeline/timeutil.py; both are tested against the same vectors
// (pipeline/tests/fixtures/time_vectors.json).

export const MILLENNIUM = 6, CENTURY = 7, DECADE = 8, YEAR = 9, MONTH = 10, DAY = 11;

export const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September",
  "October", "November", "December"];
export const MONTHS_SHORT = MONTHS.map((m) => m.slice(0, 3));

const CUM = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];

export const mod = (a: number, n: number) => ((a % n) + n) % n;
export const isLeap = (y: number) => mod(y, 4) === 0 && (mod(y, 100) !== 0 || mod(y, 400) === 0);
export const daysInYear = (y: number) => (isLeap(y) ? 366 : 365);
export const daysInMonth = (y: number, m: number) =>
  m === 2 ? (isLeap(y) ? 29 : 28) : [4, 6, 9, 11].includes(m) ? 30 : 31;
export const dayOfYear = (y: number, m: number, d: number) => CUM[m - 1] + d + (m > 2 && isLeap(y) ? 1 : 0);

export function ymdToT(y: number, m = 1, d = 1): number {
  return y + (dayOfYear(y, m, d) - 1) / daysInYear(y);
}

export function tToYmd(t: number): [number, number, number] {
  // + ~3 minutes: exported times are rounded, and a day start rounded down must not fall into the day before
  t += 0.002 / 365;
  const y = Math.floor(t);
  const doy = Math.min(Math.floor((t - y) * daysInYear(y)), daysInYear(y) - 1) + 1;
  let m = 1;
  while (m < 12 && doy > dayOfYear(y, m + 1, 1) - 1) m++;
  return [y, m, doy - (dayOfYear(y, m, 1) - 1)];
}

// --- Julian day numbers -------------------------------------------------------------------------
const fdiv = (a: number, b: number) => Math.floor(a / b);

export function gregorianToJdn(y: number, m: number, d: number): number {
  const a = fdiv(14 - m, 12), yy = y + 4800 - a, mm = m + 12 * a - 3;
  return d + fdiv(153 * mm + 2, 5) + 365 * yy + fdiv(yy, 4) - fdiv(yy, 100) + fdiv(yy, 400) - 32045;
}

export function jdnToJulian(j: number): [number, number, number] {
  const c = j + 32082, dd = fdiv(4 * c + 3, 1461), e = c - fdiv(1461 * dd, 4), mm = fdiv(5 * e + 2, 153);
  return [dd - 4800 + fdiv(mm, 10), mm + 3 - 12 * fdiv(mm, 10), e - fdiv(153 * mm + 2, 5) + 1];
}

export function julianToJdn(y: number, m: number, d: number): number {
  const a = fdiv(14 - m, 12), yy = y + 4800 - a, mm = m + 12 * a - 3;
  return d + fdiv(153 * mm + 2, 5) + 365 * yy + fdiv(yy, 4) - 32083;
}

export function jdnToGregorian(j: number): [number, number, number] {
  const a = j + 32044, b = fdiv(4 * a + 3, 146097), c = a - fdiv(146097 * b, 4);
  const dd = fdiv(4 * c + 3, 1461), e = c - fdiv(1461 * dd, 4), mm = fdiv(5 * e + 2, 153);
  return [100 * b + dd - 4800 + fdiv(mm, 10), mm + 3 - 12 * fdiv(mm, 10), e - fdiv(153 * mm + 2, 5) + 1];
}

const REFORM = ymdToT(1582, 10, 15);
const afterReform = (y: number, m: number, d: number) => y > 1582 || (y === 1582 && (m > 10 || (m === 10 && d >= 15)));

/** The date at t as it was written: Julian before the Gregorian reform of 15 October 1582 (the calendar historians
 * and Wikipedia use for earlier dates, and the site's labels of medieval battles), Gregorian from then on. */
export function civilYmd(t: number): [number, number, number] {
  const g = tToYmd(t);
  return t >= REFORM ? g : jdnToJulian(gregorianToJdn(g[0], g[1], g[2]));
}

/** When a date in that calendar begins (inverse of civilYmd). */
export function civilToT(y: number, m: number, d: number): number {
  if (afterReform(y, m, d)) return ymdToT(y, m, d);
  const [gy, gm, gd] = jdnToGregorian(julianToJdn(y, m, d));
  return ymdToT(gy, gm, gd);
}

// --- labels -------------------------------------------------------------------------------------
export interface DatePoint { y: number; m: number; d: number; prec: number; julian: boolean }

export const yearLabel = (y: number) => (y <= 0 ? `${1 - y} BC` : `${y}`);

const ordinal = (n: number) => {
  const s = n % 100 >= 10 && n % 100 <= 20 ? "th" : ({ 1: "st", 2: "nd", 3: "rd" } as Record<number, string>)[n % 10] ?? "th";
  return `${n}${s}`;
};

export function centuryStart(y: number): number {
  if (y >= 1) return fdiv(y - 1, 100) * 100 + 1;
  const bc = 1 - y;
  return 1 - (fdiv(bc - 1, 100) * 100 + 100);
}
export function millenniumStart(y: number): number {
  if (y >= 1) return fdiv(y - 1, 1000) * 1000 + 1;
  const bc = 1 - y;
  return 1 - (fdiv(bc - 1, 1000) * 1000 + 1000);
}
export function decadeStart(y: number): number {
  return y >= 1 ? fdiv(y, 10) * 10 : 1 - (fdiv(1 - y, 10) * 10 + 9);
}

export function span(dp: DatePoint): [number, number] {
  const { y, prec: p } = dp;
  if (p >= DAY) { const t0 = ymdToT(y, dp.m, dp.d); return [t0, t0 + 1 / daysInYear(y)]; }
  if (p === MONTH) {
    const [ny, nm] = dp.m === 12 ? [y + 1, 1] : [y, dp.m + 1];
    return [ymdToT(y, dp.m, 1), ymdToT(ny, nm, 1)];
  }
  if (p === YEAR) return [y, y + 1];
  if (p === DECADE) { const d0 = decadeStart(y); return [d0, d0 + 10]; }
  if (p === CENTURY) { const c0 = centuryStart(y); return [c0, c0 + 100]; }
  const m0 = millenniumStart(y);
  return [m0, m0 + 1000];
}

export function formatDate(dp: DatePoint): string {
  const { y, prec: p } = dp;
  if (p >= DAY) {
    if (dp.julian) {
      const [y2, m2, d2] = jdnToJulian(gregorianToJdn(dp.y, dp.m, dp.d));
      return `${d2} ${MONTHS[m2 - 1]} ${yearLabel(y2)}`;
    }
    return `${dp.d} ${MONTHS[dp.m - 1]} ${yearLabel(y)}`;
  }
  if (p === MONTH) return `${MONTHS[dp.m - 1]} ${yearLabel(y)}`;
  if (p === YEAR) return yearLabel(y);
  if (p === DECADE) return y >= 1 ? `${fdiv(y, 10) * 10}s` : `${fdiv(1 - y, 10) * 10}s BC`;
  if (p === CENTURY) {
    const c0 = centuryStart(y);
    return c0 >= 1 ? `${ordinal(fdiv(c0 - 1, 100) + 1)} century` : `${ordinal(fdiv(1 - c0, 100))} century BC`;
  }
  const m0 = millenniumStart(y);
  return m0 >= 1 ? `${ordinal(fdiv(m0 - 1, 1000) + 1)} millennium` : `${ordinal(fdiv(1 - m0, 1000))} millennium BC`;
}

export function formatRange(a: DatePoint | null, b: DatePoint | null, ongoing = false): string {
  if (!a) return "";
  const la = formatDate(a);
  if (ongoing) return `${la} – present`;
  if (!b) return la;
  const lb = formatDate(b);
  if (la === lb) return la;
  if (a.prec === b.prec && a.prec >= MONTH && a.y === b.y && !(a.julian || b.julian)) {
    if (a.prec >= DAY && a.m === b.m) return `${a.d} – ${b.d} ${MONTHS[a.m - 1]} ${yearLabel(a.y)}`;
    if (a.prec >= DAY) return `${a.d} ${MONTHS[a.m - 1]} – ${b.d} ${MONTHS[b.m - 1]} ${yearLabel(a.y)}`;
    return `${MONTHS[a.m - 1]} – ${MONTHS[b.m - 1]} ${yearLabel(a.y)}`;
  }
  if (a.y <= 0 && b.y <= 0 && a.prec === b.prec && b.prec === YEAR) return `${1 - a.y} – ${1 - b.y} BC`;
  return `${la} – ${lb}`;
}

/** A decimal-year instant as a label whose precision suits the zoom (span = visible years). */
export function formatInstant(t: number, spanYears: number): string {
  const [y, m, d] = civilYmd(t);
  if (spanYears <= 1.5) return `${d} ${MONTHS[m - 1]} ${yearLabel(y)}`;
  if (spanYears <= 30) return `${MONTHS[m - 1]} ${yearLabel(y)}`;
  return yearLabel(y);
}

/** Short instant label for compact UI ("12 Oct 1942", "Oct 1942", "1942", "490 BC"). */
export function formatInstantShort(t: number, spanYears: number): string {
  const [y, m, d] = civilYmd(t);
  if (spanYears <= 1.5) return `${d} ${MONTHS_SHORT[m - 1]} ${yearLabel(y)}`;
  if (spanYears <= 30) return `${MONTHS_SHORT[m - 1]} ${yearLabel(y)}`;
  return yearLabel(y);
}

/** Year span of a conflict for lists: "1939–1945", "2022–", "490–479 BC", "1914". */
export function yearsLabel(t0: number, t1: number, ongoing: boolean): string {
  const a = Math.floor(t0), b = Math.floor(t1 - 1e-6);
  if (ongoing) return `${yearLabel(a)}–`;
  if (a === b) return yearLabel(a);
  if (a <= 0 && b <= 0) return `${1 - a}–${1 - b} BC`;
  if (a <= 0) return `${1 - a} BC–${b}`;
  return `${a}–${b}`;
}

/** Duration in words: "6 years", "4 months", "12 days". */
export function durationLabel(years: number): string {
  if (years >= 1.95) return `${Math.round(years)} years`;
  if (years >= 0.95) return "1 year";
  const months = years * 12;
  if (months >= 1.5) return `${Math.round(months)} months`;
  const days = Math.max(1, Math.round(years * 365.25));
  return days === 1 ? "1 day" : `${days} days`;
}

// --- timeline ticks -----------------------------------------------------------------------------
export interface Tick { t: number; label: string; major: boolean }

const YEAR_STEPS = [1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000];

/** Ticks for the visible range [a, b] at roughly `pxPerTick` spacing over `widthPx`. */
export function ticks(a: number, b: number, widthPx: number, pxPerTick = 90): Tick[] {
  const spanY = b - a;
  const want = Math.max(2, widthPx / pxPerTick);
  const out: Tick[] = [];
  const stepY = spanY / want;
  if (stepY >= 1) {
    const step = YEAR_STEPS.find((s) => s >= stepY) ?? 1000 * Math.ceil(stepY / 1000);
    const major = step * (step >= 100 ? 5 : step >= 10 ? 5 : step >= 5 ? 2 : 5);
    // Ticks on round astronomical years; BC labels still read correctly (e.g. 500 BC is year −499).
    // Round numbers on the historical scale: AD n sits at t = n; "n BC" begins at t = 1 − n.
    for (let k = Math.floor(a / step) - 1; k * step <= b + step; k++) {
      const n = k * step;
      if (n === 0) continue; // there is no year 0 in historical numbering
      const t = n > 0 ? n : 1 + n;
      if (t < a || t > b) continue;
      out.push({ t, label: n > 0 ? `${n}` : `${-n} BC`, major: mod(n, major) === 0 });
    }
    return out;
  }
  const stepM = stepY * 12;
  if (stepM >= 1) {
    const step = [1, 2, 3, 6].find((s) => s >= stepM) ?? 6;
    const [y0] = civilYmd(a);
    for (let y = y0; y <= Math.ceil(b); y++) {
      for (let m = 1; m <= 12; m += step) {
        const t = civilToT(y, m, 1);
        if (t < a || t > b) continue;
        out.push({ t, label: m === 1 ? yearLabel(y) : MONTHS_SHORT[m - 1], major: m === 1 });
      }
    }
    return out;
  }
  const stepD = stepY * 365.25;
  const step = [1, 2, 5, 7, 14].find((s) => s >= stepD) ?? 14;
  const [y0, m0] = civilYmd(a);
  for (let y = y0, m = m0; civilToT(y, m, 1) <= b; m === 12 ? ((m = 1), y++) : m++) {
    const days = step === 7 || step === 14 ? [1, 8, 15, 22] : Array.from({ length: Math.ceil(31 / step) }, (_, i) => 1 + i * step);
    for (const d of days) {
      if (d > daysInMonth(y, m)) continue;
      const t = civilToT(y, m, d);
      if (t < a || t > b) continue;
      out.push({ t, label: d === 1 ? `${MONTHS_SHORT[m - 1]}${m === 1 ? ` ${yearLabel(y)}` : ""}` : `${d}`, major: d === 1 });
    }
  }
  return out;
}
